import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import FAQSection from '../components/FAQSection';
import { 
  Code2, 
  Bot, 
  Zap, 
  Palette, 
  Cloud, 
  ArrowRight,
  CheckCircle2,
  Smartphone,
  Globe,
  Database
} from 'lucide-react';
import { faqs } from '../data/mock';

const SoftwareAIServices = () => {
  const services = [
    {
      icon: Code2,
      category: "Custom Software Development",
      services: [
        "Web Applications",
        "Mobile Apps (iOS & Android)",
        "Enterprise Software",
        "SaaS Product Development",
        "Progressive Web Apps"
      ]
    },
    {
      icon: Bot,
      category: "AI & Automation",
      services: [
        "AI Chatbots & Virtual Assistants",
        "Workflow Automation",
        "AI Customer Support Bots",
        "AI Reporting Dashboards",
        "Predictive Analytics",
        "OCR & Document Automation"
      ]
    },
    {
      icon: Cloud,
      category: "Digital Transformation",
      services: [
        "Cloud Migration",
        "API Integrations",
        "Process Automation",
        "Data Digitization",
        "Legacy System Modernization"
      ]
    },
    {
      icon: Palette,
      category: "UI/UX Design",
      services: [
        "Product Design",
        "Web UI Design",
        "Mobile App UI",
        "User Experience Optimization"
      ]
    }
  ];

  const techStack = [
    "React", "Node.js", "Python", "Laravel", 
    "Flutter", "MongoDB", "PostgreSQL", "AWS",
    "AI/ML Tools", "TensorFlow", "Docker", "Kubernetes"
  ];

  const benefits = [
    "Scalable and secure architecture",
    "Agile development methodology",
    "Clean and maintainable code",
    "Comprehensive testing",
    "Post-deployment support",
    "Regular updates and maintenance"
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-white to-cyan-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-blue-100 text-blue-700 mb-4">Software & AI Services</Badge>
              <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                Build the Future with{' '}
                <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">
                  AI-Powered Software
                </span>
              </h1>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Transform your business with cutting-edge custom software development, AI solutions, 
                and digital transformation services designed to drive growth and efficiency.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white px-8 py-6 text-lg">
                    Discuss Your Project
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=800"
                alt="Software Development"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Our Services</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive software and AI solutions tailored to your needs
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <Card key={index} className="p-8 hover:shadow-xl transition-all duration-300 border-2 hover:border-blue-200">
                <div className="flex items-start space-x-4 mb-6">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <service.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 pt-2">{service.category}</h3>
                </div>
                <ul className="space-y-3">
                  {service.services.map((item, i) => (
                    <li key={i} className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Tech Stack Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Technologies We Use
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Latest and most reliable technologies for building robust applications
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            {techStack.map((tech, index) => (
              <Badge 
                key={index} 
                className="px-6 py-3 text-base bg-white text-gray-700 hover:bg-blue-50 hover:text-blue-600 border-2 border-gray-200 hover:border-blue-300 transition-all duration-300"
              >
                {tech}
              </Badge>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                Why Choose Our Development Services?
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                We follow industry best practices to deliver high-quality, scalable solutions 
                that drive business growth and user satisfaction.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle2 className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <Card className="p-6 text-center bg-gradient-to-br from-blue-50 to-white border-2 border-blue-100">
                <Smartphone className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Mobile First</h3>
                <p className="text-gray-600">Responsive design for all devices</p>
              </Card>
              <Card className="p-6 text-center bg-gradient-to-br from-blue-50 to-white border-2 border-blue-100">
                <Zap className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Fast</h3>
                <p className="text-gray-600">Optimized for performance</p>
              </Card>
              <Card className="p-6 text-center bg-gradient-to-br from-blue-50 to-white border-2 border-blue-100">
                <Globe className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Scalable</h3>
                <p className="text-gray-600">Grows with your business</p>
              </Card>
              <Card className="p-6 text-center bg-gradient-to-br from-blue-50 to-white border-2 border-blue-100">
                <Database className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Secure</h3>
                <p className="text-gray-600">Enterprise-grade security</p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-600 to-cyan-500">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Build Your Next Project?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Let's discuss your requirements and create something amazing together
          </p>
          <Link to="/contact">
            <Button className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-6 text-lg rounded-lg">
              Start Your Project
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQSection 
        faqs={faqs.software} 
        title="Software Development FAQs"
        subtitle="Common questions about our development services"
      />
    </div>
  );
};

export default SoftwareAIServices;
